import { defineStore } from "pinia";
import localForage from "localforage";
import axios from "axios";
import { sdk } from "@/js/sdk";
import pako from "pako";
import {
	parseTaskStatsObjectToStatsTabString,
	requireResource,
	retryWithBackoff,
	transformTabContent,
	withOutputAccessState,
} from "@/js/utilities";
import { useTasksStore } from "./tasks";
import { useBuildsStore } from "./builds";
import { isTaskTerminated } from "@/types/api/tasks";
import { isBuildTerminated } from "@/types/api/builds";
import { OutputFetchTargetE, RemoteAccessState } from "@/types/general";

interface TaskOutput {
	stderr: string;
	stdout: string;
	stdoutChunks: string[];
	timeFetchedUnix: number;
}

interface BuildOutput {
	build: string;
	timeFetchedUnix: number;
}

interface PlotOutput {
	presignedURL: string;
	timeFetchedUnix: number;
}

interface ChunkMetadata {
	totalChunks: number;
	chunksInCache: number;
}

function hasMoreChunks(chunkMetadata: undefined | ChunkMetadata): boolean {
	return chunkMetadata ? chunkMetadata.chunksInCache < chunkMetadata.totalChunks : false;
}

interface AccessTimes {
	[taskID: string]: number;
}

localForage.config({
	name: "taskOutputs",
	storeName: "outputs",
});

const LRU_TRACKING_KEY = "taskAccessTimes";
const LRU_TRACKING_KEY_PLOTS = "plotAccessTimes";
const outputUrlsExpireTimeInMinutes = 50;
const plotsUrlsExpireTimeInMinutes = 20;
const numberOfTasksToKeepAtStorage = 5;
const numberOfPlotsToKeepAtStorage = 15;

export const useOutputsStore = defineStore("outputs", {
	state: () => ({
		// State to keep track of fetched outputs
		taskOutputsUrls: {} as { [taskId: string]: TaskOutput },
		buildOutputsUrls: {} as { [buildId: string]: BuildOutput },
		// State to manage in-memory chunks for current view
		stdoutChunkMetadata: {} as { [taskId: string]: ChunkMetadata },
		// State to keep loading while fetching and transforming data.
		taskOutputIsLoadingState: {} as { [taskId: string]: boolean },
		buildOutputIsLoadingState: {} as { [buildId: string]: boolean },
		// This is what we show on panel
		stdoutStringStore: {} as { [taskId: string]: string },
		stderrStringStore: {} as { [taskId: string]: string },
		buildStringStore: {} as { [buildId: string]: string },
		statsStringStore: {} as { [taskId: string]: string },
		// How many mounted panels are showing each task, so the last one out clears its outputs.
		panelHoldersByTask: {} as { [taskId: string]: number },
		// Per-ID error state for output fetch failures. Consumed by BuildBar.
		taskOutputAccessState: {} as { [taskId: string]: RemoteAccessState },
		buildOutputAccessState: {} as { [buildId: string]: RemoteAccessState },
		stdoutChunkAccessState: {} as { [taskId: string]: RemoteAccessState },
	}),
	getters: {
		getStringStdout: (state) => (taskID?: string) => {
			const stringStdout = state.stdoutStringStore[taskID ?? ""];
			if (!stringStdout) {
				return "";
			}
			return stringStdout;
		},
		getStringStderr: (state) => (taskID?: string) => {
			const stringStderr = state.stderrStringStore[taskID ?? ""];
			if (!stringStderr) {
				return "";
			}
			return stringStderr;
		},
		getStringBuild: (state) => (buildId?: string) => {
			const stringBuild = state.buildStringStore[buildId ?? ""];
			if (!stringBuild) {
				return "";
			}
			return stringBuild;
		},
		getStringStats: (state) => (taskID?: string) => {
			const stringStats = state.statsStringStore[taskID ?? ""];
			if (!stringStats) {
				return "";
			}
			return stringStats;
		},
		getTransformedStdout: (state) => (taskID?: string) => {
			const stringStdout = state.stdoutStringStore[taskID ?? ""];
			if (!stringStdout) {
				return transformTabContent("");
			}
			return transformTabContent(stringStdout, hasMoreChunks(state.stdoutChunkMetadata[taskID ?? ""]));
		},
		getTransformedStderr: (state) => (taskID?: string) => {
			const stringStderr = state.stderrStringStore[taskID ?? ""];
			if (!stringStderr) {
				return transformTabContent("");
			}
			return transformTabContent(stringStderr);
		},
		getTransformedBuild: (state) => (buildId?: string) => {
			const stringBuild = state.stderrStringStore[buildId ?? ""];
			if (!stringBuild) {
				return transformTabContent("");
			}
			return transformTabContent(stringBuild);
		},
		getTransformedStats: (state) => (taskID?: string) => {
			const stringStats = state.buildStringStore[taskID ?? ""];
			if (!stringStats) {
				return transformTabContent("");
			}
			return transformTabContent(stringStats);
		},

		getStdoutHasMoreChunks: (state) => (taskID: string) => {
			return hasMoreChunks(state.stdoutChunkMetadata[taskID]);
		},
		getTaskLoadingState: (state) => (taskID: string) => {
			return state.taskOutputIsLoadingState[taskID] ?? false;
		},
		getBuildLoadingState: (state) => (buildID: string) => {
			return state.buildOutputIsLoadingState[buildID] ?? false;
		},
		getTaskOutputAccessState:
			(state) =>
			(taskID?: string): RemoteAccessState | undefined => {
				if (!taskID) return undefined;
				return state.taskOutputAccessState[taskID];
			},
		getBuildOutputAccessState:
			(state) =>
			(buildID?: string): RemoteAccessState | undefined => {
				if (!buildID) return undefined;
				return state.buildOutputAccessState[buildID];
			},
		getStdoutChunkAccessState:
			(state) =>
			(taskID?: string): RemoteAccessState | undefined => {
				if (!taskID) return undefined;
				return state.stdoutChunkAccessState[taskID];
			},
	},
	actions: {
		async fetchTaskOutputMetadata(taskId: string): Promise<TaskOutput> {
			return retryWithBackoff(
				async () => {
					const outputStreams = await sdk.tasks.getOutputURLs(taskId);
					return {
						stderr: outputStreams.Stderr,
						stdout: outputStreams.Stdout,
						stdoutChunks: outputStreams.StdoutChunks ?? [],
						timeFetchedUnix: Date.now(),
					};
				},
				[500, 1000, 2000],
				`Failed to fetch task output metadata after multiple attempts. Task ID: ${taskId}`,
			);
		},
		async fetchBuildOutputMetadata(buildId: string): Promise<BuildOutput> {
			return retryWithBackoff(
				async () => {
					const outputStreams = await sdk.builds.getOutputs(buildId);
					return {
						build: outputStreams.Build,
						timeFetchedUnix: Date.now(),
					};
				},
				[500, 1000, 2000],
				`Failed to fetch build output metadata after multiple attempts. Build ID: ${buildId}`,
			);
		},
		async loadBuildOutput(buildID: string, buildOutput: BuildOutput) {
			const stringBuildStoredInCache = await localForage.getItem<string>(`${buildID}-build`);
			if (stringBuildStoredInCache != null) {
				const updatedStore = { ...this.buildStringStore, [buildID]: stringBuildStoredInCache };
				this.buildStringStore = updatedStore;
				return;
			}
			const response = await axios.get(buildOutput.build);
			const buildResponse: string = response.data;
			await localForage.setItem(`${buildID}-build`, buildResponse);
			const updatedStore = { ...this.buildStringStore, [buildID]: buildResponse };
			this.buildStringStore = updatedStore;
		},
		async loadStderrOutput(taskID: string, taskOutput: TaskOutput) {
			const stringStderrStoredInCache = await localForage.getItem<string>(`${taskID}-stderr`);
			if (stringStderrStoredInCache != null) {
				const updatedStore = { ...this.stderrStringStore, [taskID]: stringStderrStoredInCache };
				this.stderrStringStore = updatedStore;
				return;
			}
			const response = await axios.get(taskOutput.stderr);
			const stderrResponse: string = response.data;
			await localForage.setItem(`${taskID}-stderr`, stderrResponse);
			const updatedStore = { ...this.stderrStringStore, [taskID]: stderrResponse };
			this.stderrStringStore = updatedStore;
		},
		async loadStatsOutput(taskID: string) {
			const stringStatsStoredInCache = await localForage.getItem<string>(`${taskID}-stats`);
			if (stringStatsStoredInCache != null) {
				const updatedStore = { ...this.buildStringStore, [taskID]: stringStatsStoredInCache };
				this.statsStringStore = updatedStore;
			} else {
				// Resolve rather than read: the task list fetch replaces taskHistory,
				// and a store miss here would cache an empty stats string for good.
				const taskData = await useTasksStore().getTaskByIdOrFetch(taskID);
				const statsString = parseTaskStatsObjectToStatsTabString(taskData.Stats);
				await localForage.setItem(`${taskID}-stats`, statsString);

				const updatedStore = { ...this.statsStringStore, [taskID]: statsString };
				this.statsStringStore = updatedStore;
			}
		},
		async loadStdoutOutput(taskID: string, taskOutput: TaskOutput) {
			const chunkMetadata = await localForage.getItem<ChunkMetadata>(`${taskID}-stdout-metadata`);
			const stringStdoutStoredInCache = await localForage.getItem<string>(`${taskID}-stdout`);

			const resultsInCache = chunkMetadata != null && stringStdoutStoredInCache != null;
			if (resultsInCache) {
				const updatedStoreChunk = { ...this.stdoutChunkMetadata, [taskID]: chunkMetadata };
				this.stdoutChunkMetadata = updatedStoreChunk;
				const updatedStore = { ...this.stdoutStringStore, [taskID]: stringStdoutStoredInCache };
				this.stdoutStringStore = updatedStore;
				return;
			}

			const noChunksFound = !taskOutput.stdoutChunks || taskOutput.stdoutChunks.length === 0;
			if (noChunksFound) {
				const response = await axios.get(taskOutput.stdout);
				const stdoutChunkResponse: string = response.data;
				await localForage.setItem(`${taskID}-stdout`, stdoutChunkResponse);

				const newChunk = {
					totalChunks: 1,
					chunksInCache: 1,
				} as ChunkMetadata;
				await localForage.setItem(`${taskID}-stdout-metadata`, newChunk);

				const updatedStoreChunk = { ...this.stdoutChunkMetadata, [taskID]: newChunk };
				this.stdoutChunkMetadata = updatedStoreChunk;

				const updatedStore = { ...this.stdoutStringStore, [taskID]: stdoutChunkResponse };
				this.stdoutStringStore = updatedStore;
				return;
			}
			const compressedChunksFound =
				taskOutput.stdoutChunks[0].includes(".gzip") || taskOutput.stdoutChunks[0].includes(".gz");

			let stdOutChunk: string;
			if (compressedChunksFound) {
				const response = await axios.get(taskOutput.stdoutChunks[0], {
					responseType: "arraybuffer",
				});
				const decompressed = pako.inflate(new Uint8Array(response.data));
				stdOutChunk = new TextDecoder("utf-8").decode(decompressed);
			} else {
				const stdoutChunkResponse = await axios.get(taskOutput.stdoutChunks[0]);
				stdOutChunk = stdoutChunkResponse.data;
			}
			await localForage.setItem(`${taskID}-stdout`, stdOutChunk);

			const newChunk = {
				totalChunks: taskOutput.stdoutChunks.length,
				chunksInCache: 1,
			} as ChunkMetadata;
			await localForage.setItem(`${taskID}-stdout-metadata`, newChunk);

			const updatedStoreChunk = { ...this.stdoutChunkMetadata, [taskID]: newChunk };
			this.stdoutChunkMetadata = updatedStoreChunk;

			const updatedStore = { ...this.stdoutStringStore, [taskID]: stdOutChunk };
			this.stdoutStringStore = updatedStore;
		},
		async loadBuildOutputs(buildID: string, skipTerminationGuard: boolean = false) {
			if (!buildID || buildID == "") {
				return;
			}
			this.buildOutputIsLoadingState = { ...this.buildOutputIsLoadingState, [buildID]: true };
			try {
				await withOutputAccessState(
					OutputFetchTargetE.Build,
					buildID,
					(state) => {
						this.buildOutputAccessState = { ...this.buildOutputAccessState, [buildID]: state };
					},
					async () => {
						if (!skipTerminationGuard) {
							// Resolve rather than read: the build list fetch replaces buildHistory.
							const buildData = await requireResource(() =>
								useBuildsStore().getBuildByIdOrFetch(buildID),
							);
							if (!isBuildTerminated(buildData.Status)) {
								return;
							}
						}
						this.updateTaskAccessTime(buildID);
						const taskOutput = await this.getBuildOutputURLs(buildID);
						await Promise.all([this.loadBuildOutput(buildID, taskOutput)]);
					},
				);
			} finally {
				this.buildOutputIsLoadingState = { ...this.buildOutputIsLoadingState, [buildID]: false };
			}
		},
		// To be used if we want to load a task
		async loadTaskOutputs(taskID: string, skipTerminationGuard: boolean = false) {
			if (!taskID || taskID == "") {
				return;
			}
			this.taskOutputIsLoadingState = { ...this.taskOutputIsLoadingState, [taskID]: true };
			try {
				await withOutputAccessState(
					OutputFetchTargetE.Task,
					taskID,
					(state) => {
						this.taskOutputAccessState = { ...this.taskOutputAccessState, [taskID]: state };
					},
					async () => {
						if (!skipTerminationGuard) {
							// Resolve rather than read: the task list fetch replaces taskHistory.
							const taskData = await requireResource(() => useTasksStore().getTaskByIdOrFetch(taskID));
							if (!isTaskTerminated(taskData.Status)) {
								return;
							}
						}
						this.updateTaskAccessTime(taskID);
						const taskOutput = await this.getTaskOutputURLs(taskID);
						await Promise.all([
							this.loadStderrOutput(taskID, taskOutput),
							this.loadStatsOutput(taskID),
							this.loadStdoutOutput(taskID, taskOutput),
						]);
					},
				);
			} finally {
				this.taskOutputIsLoadingState = { ...this.taskOutputIsLoadingState, [taskID]: false };
			}
		},
		async fetchNextChunk(taskID: string) {
			const chunkMetadata = this.stdoutChunkMetadata[taskID];
			if (!hasMoreChunks(chunkMetadata)) {
				return;
			}
			this.updateTaskAccessTime(taskID);
			this.taskOutputIsLoadingState = { ...this.taskOutputIsLoadingState, [taskID]: true };
			try {
				await withOutputAccessState(
					OutputFetchTargetE.Task,
					taskID,
					(state) => {
						this.stdoutChunkAccessState = { ...this.stdoutChunkAccessState, [taskID]: state };
					},
					async () => {
						const taskOutput = await this.getTaskOutputURLs(taskID);
						const chunkURL = taskOutput.stdoutChunks[chunkMetadata.chunksInCache];
						const isChunkCompressed = chunkURL.includes(".gzip") || chunkURL.includes(".gz");

						let newChunkStdoutString: string;
						if (isChunkCompressed) {
							const response = await axios.get(chunkURL, { responseType: "arraybuffer" });
							const decompressed = pako.inflate(new Uint8Array(response.data));
							newChunkStdoutString = new TextDecoder("utf-8").decode(decompressed);
						} else {
							const stdoutChunkResponse = await axios.get(chunkURL);
							newChunkStdoutString = stdoutChunkResponse.data;
						}

						const oldStringStdoutStoredInCache =
							(await localForage.getItem<string>(`${taskID}-stdout`)) ?? "";
						const newStdoutString = oldStringStdoutStoredInCache + newChunkStdoutString;
						const newChunkMetadata: ChunkMetadata = {
							totalChunks: taskOutput.stdoutChunks.length,
							chunksInCache: chunkMetadata.chunksInCache + 1,
						};
						await localForage.setItem(`${taskID}-stdout`, newStdoutString);
						await localForage.setItem(`${taskID}-stdout-metadata`, newChunkMetadata);

						this.stdoutStringStore = { ...this.stdoutStringStore, [taskID]: newStdoutString };
						this.stdoutChunkMetadata = { ...this.stdoutChunkMetadata, [taskID]: newChunkMetadata };
					},
				);
			} finally {
				this.taskOutputIsLoadingState = { ...this.taskOutputIsLoadingState, [taskID]: false };
			}
		},

		/**
		 * Fetches task and/or build outputs in parallel, bypassing the termination
		 * guard, and reports whether both completed without error. Callers use the
		 * `ok` result to decide whether to open a results dialog.
		 */
		async retryOutputs(params: { taskID?: string; buildID?: string }): Promise<{ ok: boolean }> {
			const fetches: Promise<unknown>[] = [];
			if (params.taskID) {
				fetches.push(this.loadTaskOutputs(params.taskID, true));
			}
			if (params.buildID) {
				fetches.push(this.loadBuildOutputs(params.buildID, true));
			}
			await Promise.allSettled(fetches);
			const taskState = params.taskID ? this.taskOutputAccessState[params.taskID] : undefined;
			const buildState = params.buildID ? this.buildOutputAccessState[params.buildID] : undefined;
			return { ok: !taskState?.error && !buildState?.error };
		},
		async getTaskOutputURLs(taskID: string): Promise<TaskOutput> {
			const outputURLs = this.taskOutputsUrls[taskID];
			if (!outputURLs) {
				const output = await this.fetchTaskOutputMetadata(taskID);
				this.taskOutputsUrls[taskID] = output;
				return output;
			}
			const difference = Math.abs(outputURLs.timeFetchedUnix - Date.now());
			const minutesDifference = Math.floor(difference / 60000);

			const expired = minutesDifference >= outputUrlsExpireTimeInMinutes;

			if (expired) {
				const output = await this.fetchTaskOutputMetadata(taskID);
				this.taskOutputsUrls[taskID] = output;
				return output;
			}
			return outputURLs;
		},
		async getBuildOutputURLs(buildID: string): Promise<BuildOutput> {
			const outputURLs = this.buildOutputsUrls[buildID];
			if (!outputURLs) {
				const output = await this.fetchBuildOutputMetadata(buildID);
				this.buildOutputsUrls[buildID] = output;
				return output;
			}
			const difference = Math.abs(outputURLs.timeFetchedUnix - Date.now());
			const minutesDifference = Math.floor(difference / 60000);

			const expired = minutesDifference >= outputUrlsExpireTimeInMinutes;

			if (expired) {
				const output = await this.fetchBuildOutputMetadata(buildID);
				this.buildOutputsUrls[buildID] = output;
				return output;
			}
			return outputURLs;
		},

		async updateTaskAccessTime(taskID: string) {
			const accessTimes = (await localForage.getItem<AccessTimes>(LRU_TRACKING_KEY)) || {};
			accessTimes[taskID] = Date.now(); // Store current time as Unix timestamp
			await localForage.setItem(LRU_TRACKING_KEY, accessTimes);
		},
		/*
		 *	A task's outputs are shared by every panel showing it, so a panel that unmounts while
		 *	another one is still open must leave the entry alone. Panels retain on mount, release on
		 *	unmount, and the release that takes the count to zero clears the outputs.
		 */
		retainTask(taskID: string) {
			if (!taskID) {
				return;
			}
			this.panelHoldersByTask[taskID] = (this.panelHoldersByTask[taskID] ?? 0) + 1;
		},

		async releaseTask(taskID: string) {
			// An unpaired release still cleans up, which is what a lone panel has always done.
			const remainingHolders = (this.panelHoldersByTask[taskID] ?? 1) - 1;
			if (taskID && remainingHolders > 0) {
				this.panelHoldersByTask[taskID] = remainingHolders;
				return;
			}
			delete this.panelHoldersByTask[taskID];
			await this.cleanUpTask(taskID);
		},

		// Clear in memory and tabs content
		async cleanUpTask(taskID: string) {
			await this.performLRUCleanup();
			delete this.stdoutStringStore[taskID];
			delete this.stderrStringStore[taskID];
			delete this.buildStringStore[taskID];
			delete this.statsStringStore[taskID];
			delete this.taskOutputIsLoadingState[taskID];
			delete this.taskOutputAccessState[taskID];
			delete this.stdoutChunkAccessState[taskID];
		},

		async performLRUCleanup() {
			// Tasks object cleanup
			await this.taskObjectsCleanup();
			// Plots cleanup
			await this.plotObjectsCleanup();
		},
		async plotObjectsCleanup() {
			const accessPlotTimes = (await localForage.getItem<AccessTimes>(LRU_TRACKING_KEY_PLOTS)) || {};
			const plotIDs = Object.keys(accessPlotTimes);

			if (plotIDs.length <= numberOfPlotsToKeepAtStorage) return;

			plotIDs.sort((a, b) => accessPlotTimes[a] - accessPlotTimes[b]);
			const plotsToRemove = plotIDs.slice(0, -numberOfPlotsToKeepAtStorage);
			console.log({ plotIDs, plotsToRemove });
			for (const plotUniqueKey of plotsToRemove) {
				await localForage.removeItem(`plot-${plotUniqueKey}`);
				delete accessPlotTimes[plotUniqueKey];
			}

			await localForage.setItem(LRU_TRACKING_KEY_PLOTS, accessPlotTimes);
		},
		async taskObjectsCleanup() {
			const accessTimes = (await localForage.getItem<AccessTimes>(LRU_TRACKING_KEY)) || {};
			const taskIDs = Object.keys(accessTimes);

			if (taskIDs.length <= numberOfTasksToKeepAtStorage) return;

			taskIDs.sort((a, b) => accessTimes[a] - accessTimes[b]);
			const tasksToRemove = taskIDs.slice(0, -numberOfTasksToKeepAtStorage);

			for (const taskID of tasksToRemove) {
				await localForage.removeItem(`${taskID}-stdout-metadata`);
				await localForage.removeItem(`${taskID}-stdout`);
				await localForage.removeItem(`${taskID}-stderr`);
				await localForage.removeItem(`${taskID}-stats`);
				delete accessTimes[taskID];
			}

			await localForage.setItem(LRU_TRACKING_KEY, accessTimes);
		},
		async updatePlotAccessTime(uniquePlotKey: string) {
			const accessTimes = (await localForage.getItem<AccessTimes>(LRU_TRACKING_KEY_PLOTS)) || {};
			accessTimes[uniquePlotKey] = Date.now();
			await localForage.setItem(LRU_TRACKING_KEY_PLOTS, accessTimes);
		},
		async getUxStringPlot(uxString: string) {
			const uniquePlotKey = uxString;
			const cachedPlotURL = await localForage.getItem<PlotOutput>(`plot-${uniquePlotKey}`);
			if (cachedPlotURL) {
				const difference = Math.abs(cachedPlotURL.timeFetchedUnix - Date.now());
				const minutesDifference = Math.floor(difference / 60000);

				const expired = minutesDifference >= plotsUrlsExpireTimeInMinutes;
				if (!expired) {
					this.updatePlotAccessTime(uniquePlotKey);
					return cachedPlotURL.presignedURL;
				}
			}
			const resp = await sdk.plotting.plot(uxString);
			const plotOutput = {
				presignedURL: resp.presignedURL,
				timeFetchedUnix: Date.now(),
			} as PlotOutput;
			await localForage.setItem(`plot-${uniquePlotKey}`, plotOutput);
			this.updatePlotAccessTime(uniquePlotKey);
			return resp.presignedURL;
		},
		async getValueIdPlot(taskID: string, valueID: string) {
			const uniquePlotKey = taskID + valueID;
			const cachedPlotURL = await localForage.getItem<PlotOutput>(`plot-${uniquePlotKey}`);
			if (cachedPlotURL) {
				const difference = Math.abs(cachedPlotURL.timeFetchedUnix - Date.now());
				const minutesDifference = Math.floor(difference / 60000);

				const expired = minutesDifference >= plotsUrlsExpireTimeInMinutes;
				if (!expired) {
					this.updatePlotAccessTime(uniquePlotKey);
					return cachedPlotURL.presignedURL;
				}
			}
			const resp = await sdk.plotting.plotValue(taskID, valueID);
			const plotOutput = {
				presignedURL: resp.presignedURL,
				timeFetchedUnix: Date.now(),
			} as PlotOutput;
			await localForage.setItem(`plot-${uniquePlotKey}`, plotOutput);
			this.updatePlotAccessTime(uniquePlotKey);
			return resp.presignedURL;
		},
		async purgeCache() {
			try {
				const keys = await localForage.keys();
				const outputKeys = keys.filter(
					(key) =>
						key.includes("-stdout") ||
						key.includes("-stderr") ||
						key.includes("-build") ||
						key.includes("-stats") ||
						key.includes("plot-") ||
						key === LRU_TRACKING_KEY ||
						key === LRU_TRACKING_KEY_PLOTS,
				);
				for (const key of outputKeys) {
					await localForage.removeItem(key);
				}
			} catch (error) {
				console.error("Failed to purge cache:", error);
			}

			this.$reset();
		},
	},
});
